import React from 'react';
import { Tabs, Radio } from 'antd';
import { AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import type { TabListProps } from './types';
import { useTabList } from './useTabList';
import PageList from '../../../components/Templates/PageList';

const TabListHeader: React.FC<{ title?: string }> = ({ title }) => (
  <div style={{
    display: 'flex',
    background: '#e6f7ff',
    padding: '12px 0',
    fontWeight: 'bold',
    borderRadius: '4px 4px 0 0',
    marginBottom: 0,
    borderBottom: '1px solid #f0f0f0'
  }}>
    <div style={{ flex: 1, paddingLeft: 16 }}>{title || '列表'}</div>
  </div>
);

function TabListTemplate<T extends object>(props: TabListProps<T>) {
  const {
    queryFields,
    fetchList,
    viewModes,
    pageListKey,
    title,
    onExport,
  } = props;

  const {
    listRef,
    initialState,
    initFinished,
    kpiStats,
    tabStats,
    activeIndex,
    currentTabKey,
    currentTabKeyRef,
    currentViewMode,
    setCurrentViewMode,
    handleTabChange,
    refreshStats,
  } = useTabList(props);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = React.useState<T[]>([]);

  /** 使用 ref 存储最新的列表数据，避免在渲染期间直接调用 setState */
  const latestRowsRef = React.useRef<T[]>([]);
  const selectedRowKeysRef = React.useRef<React.Key[]>([]);
  /** 追踪 rows 引用是否变化，用于 useEffect 安全同步已选行数据 */
  const prevRowsRef = React.useRef<T[]>([]);
  const [rowsChanged, setRowsChanged] = React.useState(0);

  const clearSelection = React.useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    selectedRowKeysRef.current = [];
  }, []);

  React.useEffect(() => {
    clearSelection();
  }, [currentTabKey, currentViewMode, clearSelection]);

  /**
   * 当列表数据引用真正变化时，如果当前有已勾选项且 viewMode 有配置 rowKey，
   * 从最新 rows 中重新匹配已勾选行，确保批量操作拿到的是最新数据。
   */
  React.useEffect(() => {
    if (selectedRowKeysRef.current.length === 0 || latestRowsRef.current.length === 0) return;
    const rowKeyFn = (viewModes.find(v => v.mode === currentViewMode) || viewModes[0])?.rowKey;
    if (!rowKeyFn) return;

    const keySet = new Set(selectedRowKeysRef.current.map(String));
    const refreshedRows = latestRowsRef.current.filter(row => keySet.has(String(rowKeyFn(row))));

    setSelectedRows(prev => {
      const prevKeys = prev.map(r => String(rowKeyFn(r))).sort().join(',');
      const nextKeys = refreshedRows.map(r => String(rowKeyFn(r))).sort().join(',');
      return prevKeys === nextKeys ? prev : refreshedRows;
    });
  }, [rowsChanged]); // eslint-disable-line



  const isFirstFetchRef = React.useRef(true);


  const renderRadioGroup = () => (
    viewModes.length > 1 ? (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Radio.Group
          value={currentViewMode}
          onChange={e => setCurrentViewMode(e.target.value)}
          buttonStyle="solid"
          size="small"
        >
          {viewModes.map(vm => (
            <Radio.Button key={vm.mode} value={vm.mode}>
              {vm.mode === 'table' ? <AppstoreOutlined /> : <BarsOutlined />} {vm.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
    ) : null
  );

  if (!initFinished) return null;

  const activeStat = tabStats.length > 0 ? tabStats[Number(activeIndex)] : undefined;
  const currentViewConfig = viewModes.find(v => v.mode === currentViewMode) || viewModes[0];

  const batchActionNode = currentViewConfig?.renderBatchActions?.(
    selectedRows,
    clearSelection,
    currentTabKey
  );

  const isSelectionEnabled = !!batchActionNode;

  return (
    <PageList<T>
      key={pageListKey || 'tab-list'}
      ref={listRef as any}
      queryFields={queryFields}
      layoutConfig={{ pagination: 'fixed' }}
      ignoreRemembered={!!initialState.forcedQuery}
      syncToUrl={{
        extraParams: {
          t: currentTabKey,
          v: currentViewMode === (viewModes[0]?.mode || 'table') ? undefined : currentViewMode
        }
      }}
      defaultQuery={initialState.initialQuery}
      onExport={onExport ? (query) => {
        // 合并当前 tab 场景
        const mergedQuery = { ...query };
        if (currentTabKey) {
          (mergedQuery as any).scene = currentTabKey;
        }
        onExport(mergedQuery);
      } : undefined}
      fetcher={async (params: any) => {
        if (isFirstFetchRef.current) {
          isFirstFetchRef.current = false;
        } else {
          refreshStats({ filter: params.filter, sort: params.sort });
        }
        // 使用 ref 读取最新的 tabKey，避免闭包陈旧值导致切换 tab 时请求上一个 tab 的数据
        return await fetchList(params, currentTabKeyRef.current);
      }}
      buildParams={(q) => q as any}
      kpiItems={kpiStats.map(s => ({
        title: s.label,
        value: Number(s.value) || 0,
      }))}
      headerExtra={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          {tabStats.length > 0 ? (
            <Tabs
              activeKey={activeIndex}
              onChange={handleTabChange}
              items={tabStats.map((s, index) => ({
                label: `${s.label} (${s.value})`,
                key: String(index)
              }))}
              style={{ flex: 1 }}
              tabBarExtraContent={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  {batchActionNode}
                  {renderRadioGroup()}
                </div>
              }
            />
          ) : (
            <>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {batchActionNode}
                {renderRadioGroup()}
              </div>
            </>
          )}
        </div>
      }
      renderList={({ rows }) => {
        if (!currentViewConfig) {
          return <div>无效的视图配置</div>;
        }

        // 仅当 rows 引用真正变化时，才通过 state 触发已选行同步
        if (rows !== prevRowsRef.current) {
          prevRowsRef.current = rows;
          latestRowsRef.current = rows;
          // 使用 setTimeout 代替 queueMicrotask，确保不在 render 中直接 setState
          // 且仅在引用变化时触发，不会无限递增
          Promise.resolve().then(() => setRowsChanged(c => c + 1));
        }

        return currentViewConfig.renderList({
          rows,
          activeTabKey: currentTabKey,
          activeStat,
          selectedRowKeys: isSelectionEnabled ? selectedRowKeys : undefined,
          onChangeSelection: isSelectionEnabled ? (keys, selectedRowsList) => {
            setSelectedRowKeys(keys);
            setSelectedRows(selectedRowsList);
            selectedRowKeysRef.current = keys;
          } : undefined
        });
      }}
    />
  );
}

export default TabListTemplate;
